import React, { useState, useEffect, useRef } from 'react';
import { ACTIVE_BRAND } from '../branding';
import './ScanningPage.css';

function ScanningPage({
  apiEndpoints,
  applicationTitle,
  serialNumber,
  timeout,
  onComplete,
  onCancel,
  onSelfieReady,
  setApplicationId,
  setRequestId
}) {
  const [status, setStatus] = useState('Initializing...');
  const [statusCode, setStatusCode] = useState(0);
  // Ensure timeout is a valid positive number, otherwise default to 15
  const safeTimeout = (typeof timeout === 'number' && !isNaN(timeout) && timeout > 0) ? timeout : 15;
  const [countdown, setCountdown] = useState(safeTimeout);
  const [userPrompt, setUserPrompt] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const pollingIntervalRef = useRef(null);
  const appIdRef = useRef('');
  const reqIdRef = useRef('');
  const hasStarted = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (!hasStarted.current) {
      hasStarted.current = true;
      startWorkflow();
    }
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    updateUserPrompt(statusCode);
  }, [statusCode]);

  const addLog = (message) => {
    setActivityLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const updateUserPrompt = (code) => {
    const prompts = {
      0: 'Initializing...',
      1: 'Connecting to scanner...',
      2: 'Please place your ID on the scanner',
      3: 'Document detected on scanner',
      4: 'Scanning in progress...',
      5: 'Scan completed successfully',
      6: 'Uploading scan data...',
      7: 'Processing your document...',
      8: 'Scan timeout - please try again',
      9: 'Scan canceled',
      10: 'Please remove your document'
    };
    setUserPrompt(prompts[code] || 'Processing...');
  };

  const startWorkflow = async () => {
    try {
      addLog('Starting E-Kiosk workflow...');

      if (process.env.NODE_ENV !== 'production') {
        console.log('API Endpoints:', apiEndpoints);
        console.log('executeWorkflow URL:', apiEndpoints.executeWorkflow);
        console.log('Request body:', { application_title: applicationTitle, serial_number: serialNumber, timeout: safeTimeout });
      }

      const response = await fetch(apiEndpoints.executeWorkflow, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_title: applicationTitle,
          serial_number: serialNumber,
          timeout: safeTimeout
        })
      });

      const data = await response.json();
      if (process.env.NODE_ENV !== 'production') {
        console.log('Workflow response:', data);
      }

      // Extract Application_ID and Request_ID from various possible response structures
      let applicationId = null;
      let requestId = null;

      // Check different possible locations in the response
      if (data.data && data.data.application_id && data.data.request_id) {
        // New structure: {success: true, data: {application_id: "...", request_id: "..."}}
        applicationId = data.data.application_id;
        requestId = data.data.request_id;
      } else if (data.Result && data.Result.Application_ID && data.Result.Request_ID) {
        // Old structure: {Result: {Application_ID: "...", Request_ID: "..."}}
        applicationId = data.Result.Application_ID;
        requestId = data.Result.Request_ID;
      } else if (data.Application_ID && data.Request_ID) {
        // Direct structure: {Application_ID: "...", Request_ID: "..."}
        applicationId = data.Application_ID;
        requestId = data.Request_ID;
      }

      if (applicationId && requestId) {
        appIdRef.current = applicationId;
        reqIdRef.current = requestId;
        setApplicationId(applicationId);
        setRequestId(requestId);

        addLog(`Application ID: ${applicationId}`);
        addLog(`Request ID: ${requestId}`);
        addLog('Workflow initiated, starting status monitoring...');

        // Start polling, will navigate to selfie when status is 7 (finished scanning)
        startPolling(applicationId, requestId);
      } else {
        addLog('ERROR: Failed to get Application ID or Request ID');
        addLog(`Response data: ${JSON.stringify(data).substring(0, 500)}...`);
        setStatus('Error starting workflow');
      }
    } catch (error) {
      addLog(`ERROR: ${error.message}`);
      setStatus('Error starting workflow');
    }
  };

  const startPolling = (appId, reqId) => {
    pollingIntervalRef.current = setInterval(() => {
      pollStatus(appId, reqId);
    }, 1000);
  };

  const pollStatus = async (appId, reqId) => {
    try {
      const response = await fetch(
        `${apiEndpoints.checkStatus}?application_id=${appId}&request_id=${reqId}`
      );

      const data = await response.json();

      // Handle different response structures
      let statusInfo = null;

      // Check for status_info at top level (what check_status Lambda returns)
      if (data.status_info) {
        statusInfo = {
          status: data.status_info.status_text || 'Processing...',
          status_code: parseInt(data.status_info.status_code) || 0,
          is_complete: data.status_info.is_complete || false,
          is_error: data.status_info.is_error || false
        };
      } else if (data.data && data.data.status_info) {
        statusInfo = data.data.status_info;
      } else if (data.data && data.data.Result) {
        // Fallback: parse from raw Result if status_info missing
        const result = data.data.Result;
        const statusCodeNum = parseInt(result.Status_Code) || 0;
        statusInfo = {
          status: result.Status_Text || 'Processing...',
          status_code: statusCodeNum,
          is_complete: statusCodeNum === 7 || statusCodeNum === 8 || statusCodeNum === 9,
          is_error: statusCodeNum === 8 || statusCodeNum === 9
        };
      } else if (data.Result) {
        // Direct result from Lambda: {Result: {...}}
        const statusCodeNum = parseInt(data.Result.Status_Code) || 0;
        statusInfo = {
          status: data.Result.Status_Text || data.Result.Last_Action || 'Processing...',
          status_code: statusCodeNum,
          is_complete: statusCodeNum === 7 || statusCodeNum === 8 || statusCodeNum === 9,
          is_error: statusCodeNum === 8 || statusCodeNum === 9
        };
      }

      if (statusInfo) {
        setStatus(statusInfo.status);
        setStatusCode(statusInfo.status_code);

        // Log status changes (only if changed)
        if (statusInfo.status_code !== statusCode) {
          addLog(`Status: ${statusInfo.status} (Code: ${statusInfo.status_code})`);
        }

        // Countdown only before document is detected
        if (statusInfo.status_code === 2) {
          setCountdown(prev => Math.max(0, prev - 1));
        }

        if (statusInfo.is_complete) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
          }

          if (statusInfo.status_code === 7) {
            addLog('Scan complete! Navigating to selfie capture...');
            // Navigate to selfie page now that document is captured
            if (onSelfieReady) {
              onSelfieReady();
            }
          } else if (statusInfo.is_error) {
            addLog(`Scan ended with error: ${statusInfo.status}`);
            setStatus('Scan failed');
          }
        }
      }
    } catch (error) {
      addLog(`Polling error: ${error.message}`);
    }
  };

  const executeAndGetResults = async (appId) => {
    try {
      // First execute with Force_ReExecute
      addLog('Executing with Force_ReExecute=True...');
      const exec1Response = await fetch(apiEndpoints.execute, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: appId,
          force_reexecute: true
        })
      });
      const exec1Data = await exec1Response.json();
      addLog(`Execute 1 complete: ${exec1Data.success ? 'Success' : 'Failed'}`);

      // Wait a bit before second execute
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Second execute without Force_ReExecute
      addLog('Executing without Force_ReExecute...');
      const exec2Response = await fetch(apiEndpoints.execute, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: appId,
          force_reexecute: false
        })
      });
      const exec2Data = await exec2Response.json();
      addLog(`Execute 2 complete: ${exec2Data.success ? 'Success' : 'Failed'}`);

      // Wait a bit before getting results
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Get results
      addLog('Fetching final results...');
      const resultsResponse = await fetch(apiEndpoints.getResults, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          application_id: appId
        })
      });
      const resultsData = await resultsResponse.json();

      // Handle nested response structure
      const result = resultsData.data?.Result || resultsData.Result;

      if (result && result.Elements && result.Elements.List) {
        addLog(`Found ${result.Elements.Count} elements in results`);

        // Elements.List is an object with Element_No_X keys, not an array
        // Find the element with Key="DLF" (Driving Licence Front) which has Classification
        let classification = null;

        for (let key in result.Elements.List) {
          const element = result.Elements.List[key];
          if (element.Key === 'DLF' && element.Classification) {
            classification = element.Classification;
            addLog(`Classification found in ${key}: ${element.Title}`);
            break;
          }
        }

        if (classification) {
          addLog('Classification data extracted successfully!');
          onComplete(classification);
        } else {
          addLog('No DLF element with classification found');
          onComplete(null);
        }
      } else {
        addLog('Invalid response structure - no Elements.List found');
        onComplete(null);
      }
    } catch (error) {
      addLog(`Error getting results: ${error.message}`);
      onComplete(null);
    }
  };

  const handleCancel = async () => {
    // Stop polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Call cancel API if we have IDs
    if (appIdRef.current && reqIdRef.current) {
      try {
        addLog('Canceling scan...');
        const response = await fetch(apiEndpoints.cancelScan, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            application_id: appIdRef.current,
            request_id: reqIdRef.current
          })
        });

        const data = await response.json();
        addLog(`Cancel response: ${JSON.stringify(data).substring(0, 200)}`);
      } catch (error) {
        addLog(`Error canceling scan: ${error.message}`);
      }
    }

    // Navigate back
    onCancel();
  };

  return (
    <div className="scanning-page">
      <div className="scanning-content">
        <div className="scanning-card">
          <div className="scanner-icon">📷</div>

          <h2 className="user-prompt" style={{ color: ACTIVE_BRAND.colors.primary }}>
            {userPrompt}
          </h2>

          <div className="status-indicator">
            <div className="status-bar">
              <div
                className="status-progress"
                style={{
                  width: `${(statusCode / 7) * 100}%`,
                  backgroundColor: ACTIVE_BRAND.colors.secondary
                }}
              />
            </div>
            <p className="status-text">{status}</p>
          </div>

          {statusCode < 3 && statusCode > 0 && (
            <div className="countdown">
              <span className="countdown-number">{countdown}</span>
              <span className="countdown-label">seconds remaining</span>
            </div>
          )}

          <button
            className="cancel-scan-button"
            onClick={handleCancel}
            style={{
              borderColor: ACTIVE_BRAND.colors.buttonBg,
              color: ACTIVE_BRAND.colors.buttonBg
            }}
          >
            CANCEL SCAN
          </button>
        </div>
      </div>
    </div>
  );
}

export default ScanningPage;
